import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Divider,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";

const ALTO_TACTIL = 48;

const sxBoton = {
    textTransform: "none",
    fontWeight: 600,
    minHeight: ALTO_TACTIL,
    py: { xs: 1.25, sm: 1 },
    fontSize: { xs: 14, sm: 15 },
};

const sxPrimario = {
    ...sxBoton,
    backgroundColor: "#cc071e",
    "&:hover": { backgroundColor: "#a30618" },
    "&:disabled": { backgroundColor: "#6c757d", color: "#fff" },
};

const sxSecundario = { ...sxBoton, color: "#6c757d", borderColor: "#6c757d" };

const MOTIVOS_NO_CERRAR = ["backdropClick", "escapeKeyDown"];

const spinnerRojo = <CircularProgress size={16} sx={{ color: "#cc071e" }} />;
const spinnerBlanco = <CircularProgress size={16} sx={{ color: "#fff" }} />;

/**
 * Modal para elegir entre registrar solo el pedido introducido o todos los pedidos
 * pendientes del cliente. Presentacional y sin estado: `onConfirm(incluirTodos)`
 * lo resuelve la página. `eleccionEnCurso` (true/false/null) indica qué botón
 * muestra el spinner mientras `loading`. Equivale al backdrop estático del legacy:
 * solo se cierra con la X o Cancelar.
 */
export default function ModalPedidosCliente({
    show,
    onHide,
    onConfirm,
    numero,
    loading = false,
    eleccionEnCurso = null,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const handleClose = (_event, motivo) => {
        if (loading || MOTIVOS_NO_CERRAR.includes(motivo)) return;
        onHide();
    };

    const cargandoSolo = loading && eleccionEnCurso === false;
    const cargandoTodos = loading && !cargandoSolo;

    return (
        <Dialog
            open={show}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: { borderRadius: isMobile ? 0 : 2, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", m: isMobile ? 0 : 2 },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    pb: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 3 },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                    <Box
                        sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: "50%",
                            backgroundColor: "#fee",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <PeopleAltIcon sx={{ color: "#cc071e", fontSize: { xs: 20, sm: 24 } }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: "#212529", fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
                        {isMobile ? "¿Todos los pedidos?" : "¿Incluir todos los pedidos del cliente?"}
                    </Typography>
                </Box>
                <IconButton onClick={onHide} disabled={loading} size="small" aria-label="Cerrar">
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent
                sx={{ pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, px: { xs: 2, sm: 3 }, flex: isMobile ? 1 : "initial" }}
            >
                <Typography sx={{ fontSize: { xs: 15, sm: 16 }, color: "#495057", lineHeight: 1.6 }}>
                    Has introducido el pedido <strong>{String(numero ?? "").trim() || "—"}</strong>. ¿Quieres poner en
                    cola solo este pedido o todos los pedidos pendientes del cliente?
                </Typography>
            </DialogContent>

            <Divider />

            <DialogActions
                sx={{ p: { xs: 2, sm: 2.5 }, gap: { xs: 1, sm: 1.5 }, flexDirection: { xs: "column-reverse", sm: "row" } }}
            >
                <Button variant="outlined" onClick={onHide} fullWidth={isMobile} disabled={loading} sx={sxSecundario}>
                    Cancelar
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => onConfirm(false)}
                    fullWidth={isMobile}
                    disabled={loading}
                    startIcon={cargandoSolo ? spinnerRojo : null}
                    sx={sxSecundario}
                >
                    {cargandoSolo ? "Registrando..." : "Solo este pedido"}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => onConfirm(true)}
                    fullWidth={isMobile}
                    disabled={loading}
                    startIcon={cargandoTodos ? spinnerBlanco : null}
                    sx={sxPrimario}
                >
                    {cargandoTodos ? "Registrando..." : "Todos los pedidos del cliente"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
